import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { AlertCircle, ChevronLeft, ChevronRight, Clock, DollarSign, ChevronUp, ChevronDown, Loader2, RefreshCw, Wifi, WifiOff, Radio } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { vmTradingService } from '@/services/vmTradingService';
import { deploymentService } from '@/services/deploymentService';

// Define the TradeActivity type directly here if it's missing from the types file
interface TradeActivity {
  id: string;
  strategy_id: string;
  symbol: string;
  action: 'buy' | 'sell';
  quantity: number;
  price: number;
  timestamp: string;
  status: 'pending' | 'filled' | 'canceled' | 'rejected' | 'executed' | 'open' | 'cancelled' | 'failed' | 'closed';
  order_type: string;
  execution_type: 'market' | 'limit' | 'stop' | 'stop_limit';
  [key: string]: any;
}

interface TradingActivityProps {
  strategyId?: string; // Made optional to support different modes
  mode?: 'strategy' | 'manual' | 'all'; // New mode prop
  title?: string; // Custom title
}

interface TradeFilters {
  status: TradeActivity['status'] | 'all';
  sortBy: 'timestamp' | 'symbol' | 'price' | 'quantity';
  sortOrder: 'asc' | 'desc';
}

interface LiveUpdateState {
  isLive: boolean;
  lastUpdate: Date | null;
  wsConnected: boolean;
  pollingActive: boolean;
  updateCount: number;
}

export const TradingActivity: React.FC<TradingActivityProps> = ({
  strategyId,
  mode = 'strategy', // Default to strategy mode for backward compatibility
  title
}) => {
  const [trades, setTrades] = useState<TradeActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vmSimulationMode, setVmSimulationMode] = useState(false);
  const [filters, setFilters] = useState<TradeFilters>({
    status: 'all',
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const tradesPerPage = 10;

  // Live updates state
  const [liveState, setLiveState] = useState<LiveUpdateState>({
    isLive: false,
    lastUpdate: null,
    wsConnected: false,
    pollingActive: false,
    updateCount: 0
  });

  // Refs for cleanup and polling control
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wsHandlersRegisteredRef = useRef(false);
  const componentMountedRef = useRef(true);

  // Update the status type to match our expanded TradeActivity interface
  const [statusFilter, setStatusFilter] = useState<TradeActivity['status'] | 'all'>('all');

  // Helper function to safely parse numeric values - hoisted outside of map for performance, handles thousands separators
  const safeParseFloat = (value: any, defaultValue: number = 0): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remove commas and other thousands separators
      const cleanValue = value.replace(/,/g, '');
      const parsed = parseFloat(cleanValue);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  };

  // Live data update handler for WebSocket messages
  const handleLiveOrderUpdate = useCallback((orderData: any) => {
    if (!componentMountedRef.current) return;

    console.log('[TradingActivity] Live order update received:', orderData);
    
    setLiveState(prev => ({
      ...prev,
      lastUpdate: new Date(),
      updateCount: prev.updateCount + 1
    }));

    // Convert single order or array of orders to TradeActivity format
    const orders = Array.isArray(orderData) ? orderData : [orderData];
    
    const newTrades: TradeActivity[] = orders.map((order, index) => ({
      id: order.id || `live_order_${Date.now()}_${index}`,
      strategy_id: order.strategy_id || strategyId || 'vm_strategy',
      symbol: order.symbol || 'UNKNOWN',
      action: (order.side === 'buy' || order.side === 'sell') ? order.side : 'buy',
      quantity: Math.round(safeParseFloat(order.qty || order.quantity, 1)),
      price: safeParseFloat(order.filled_avg_price || order.price || order.entry_price, 0),
      timestamp: order.created_at || order.timestamp || order.entry_timestamp || new Date().toISOString(),
      status: order.status as TradeActivity['status'] || 'pending',
      order_type: order.type || order.order_type || 'market',
      execution_type: order.type || order.execution_type || 'market',
      // Additional metadata
      order_source: order.source || 'vm_live',
      order_id: order.id,
      limit_price: safeParseFloat(order.limit_price),
      stop_price: safeParseFloat(order.stop_price),
      is_live_update: true // Mark as live update
    }));

    // Update trades state with new live data
    setTrades(prevTrades => {
      const updatedTrades = [...prevTrades];
      
      // Update existing orders or add new ones
      newTrades.forEach(newTrade => {
        const existingIndex = updatedTrades.findIndex(t => t.id === newTrade.id);
        if (existingIndex >= 0) {
          updatedTrades[existingIndex] = newTrade; // Update existing
        } else {
          updatedTrades.unshift(newTrade); // Add new at beginning
        }
      });

      // Apply mode filtering
      let filteredTrades = updatedTrades;
      if (mode === 'strategy' && strategyId) {
        filteredTrades = updatedTrades.filter(trade => trade.strategy_id === strategyId);
      } else if (mode === 'manual') {
        filteredTrades = updatedTrades.filter(trade => 
          trade.strategy_id === 'manual' || trade.order_source === 'manual' || trade.order_source === 'stewietrader_ui'
        );
      }

      return filteredTrades;
    });
  }, [strategyId, mode]);

  // Setup WebSocket connection for live updates
  const setupLiveUpdates = useCallback(async () => {
    try {
      // Check if WebSocket handlers already registered
      if (wsHandlersRegisteredRef.current) {
        console.log('[TradingActivity] WebSocket handlers already registered');
        return;
      }

      // Check if WebSocket is already connected
      const wsStatus = vmTradingService.getWebSocketStatus();
      if (!wsStatus.connected) {
        console.log('[TradingActivity] Connecting to VM WebSocket for live updates...');
        await vmTradingService.connectWebSocket();
      }

      // Register handlers for live order updates
      vmTradingService.onWebSocketMessage('order_update', handleLiveOrderUpdate);
      vmTradingService.onWebSocketMessage('portfolio_update', (data) => {
        // Portfolio updates might contain order information
        if (data.orders) {
          handleLiveOrderUpdate(data.orders);
        }
      });

      wsHandlersRegisteredRef.current = true;
      
      setLiveState(prev => ({
        ...prev,
        wsConnected: true,
        isLive: true
      }));

      console.log('[TradingActivity] Live WebSocket updates enabled');
      
    } catch (error) {
      console.warn('[TradingActivity] WebSocket setup failed, falling back to polling:', error);
      setupPolling();
    }
  }, [handleLiveOrderUpdate]);

  // Polling update function (separate from fetchTradingActivity to avoid circular deps)
  const pollForUpdates = useCallback(async () => {
    if (!componentMountedRef.current) return;

    try {
      console.log('[TradingActivity] Polling for order updates...');
      
      // Fetch both all orders and filled orders specifically to ensure we get trading history
      const [paperOrders, liveOrders, paperFilledOrders, liveFilledOrders, paperPositions, livePositions] = await Promise.all([
        vmTradingService.getOrders('PAPER').catch(err => { console.warn('Polling PAPER orders failed:', err); return []; }),
        vmTradingService.getOrders('LIVE').catch(err => { console.warn('Polling LIVE orders failed:', err); return []; }),
        vmTradingService.getOrders('PAPER', 'filled').catch(err => { console.warn('Polling PAPER filled orders failed:', err); return []; }),
        vmTradingService.getOrders('LIVE', 'filled').catch(err => { console.warn('Polling LIVE filled orders failed:', err); return []; }),
        vmTradingService.getPositions('PAPER').catch(err => { console.warn('Polling PAPER positions failed:', err); return []; }),
        vmTradingService.getPositions('LIVE').catch(err => { console.warn('Polling LIVE positions failed:', err); return []; })
      ]);

      // Merge all orders and filled orders (remove duplicates by ID)
      const allPaperOrders = [...paperOrders, ...paperFilledOrders].filter((order, index, self) => 
        index === self.findIndex(o => o.id === order.id)
      );
      const allLiveOrders = [...liveOrders, ...liveFilledOrders].filter((order, index, self) => 
        index === self.findIndex(o => o.id === order.id)
      );

      const taggedPaperOrders = allPaperOrders.map(o => ({ ...o, _tradingMode: 'PAPER' }));
      const taggedLiveOrders = allLiveOrders.map(o => ({ ...o, _tradingMode: 'LIVE' }));

      const allOrders = [...taggedPaperOrders, ...taggedLiveOrders];
      const allPositions = [...paperPositions, ...livePositions];

      console.log(`[TradingActivity] Polling - Orders: ${allOrders.length}, Positions: ${allPositions.length}`);

      // Transform and update trades from orders
      const fetchedTrades: TradeActivity[] = [];
      
      if (allOrders.length > 0) {
        const orderTrades = allOrders.map((order, index) => {
          const associatedPosition = allPositions.find(p => p.symbol === order.symbol);
          const tradingMode = order._tradingMode;

          let orderPrice = 0;
          if (order.filled_avg_price) {
            orderPrice = safeParseFloat(order.filled_avg_price);
          } else if (associatedPosition?.avg_entry_price) {
            orderPrice = safeParseFloat(associatedPosition.avg_entry_price);
          } else if (order.price) {
            orderPrice = safeParseFloat(order.price);
          }

          return {
            id: order.id || `vm_order_${index}`,
            strategy_id: order.strategy_id || strategyId || 'vm_strategy',
            symbol: order.symbol || 'UNKNOWN',
            action: (order.side === 'buy' || order.side === 'sell') ? order.side : 'buy',
            quantity: Math.round(safeParseFloat(order.qty || order.quantity, 1)),
            price: orderPrice,
            timestamp: order.created_at || order.timestamp || order.entry_timestamp || new Date().toISOString(),
            status: order.status as TradeActivity['status'] || 'pending',
            order_type: order.type || order.order_type || 'market',
            execution_type: order.type || order.execution_type || 'market',
            order_source: order.source || 'vm_trading',
            order_id: order.id,
            limit_price: safeParseFloat(order.limit_price),
            stop_price: safeParseFloat(order.stop_price),
            trading_mode: tradingMode || 'UNKNOWN',
          };
        });
        
        fetchedTrades.push(...orderTrades);
        console.log(`[TradingActivity] Polling - Created ${orderTrades.length} trades from orders`);
      }

      // If no orders but we have positions, create TradeActivity objects from positions
      if (allOrders.length === 0 && allPositions.length > 0) {
        console.log(`[TradingActivity] No orders found, creating trades from ${allPositions.length} positions`);
        
        const positionTrades: TradeActivity[] = [];
        
        // Process PAPER positions
        paperPositions.forEach((position, index) => {
          const stableId = `position_${position.asset_id || position.symbol}_PAPER`;
          const stableTimestamp = position.created_at || '2025-06-17T13:23:45.000Z';
          
          positionTrades.push({
            id: stableId,
            strategy_id: position.strategy_id || 'vm_strategy',
            symbol: position.symbol || 'UNKNOWN',
            action: (parseFloat(position.qty || '0') > 0 ? 'buy' : 'sell') as 'buy' | 'sell',
            quantity: Math.abs(Math.round(safeParseFloat(position.qty || position.quantity, 1))),
            price: safeParseFloat(position.avg_entry_price || position.current_price, 0),
            timestamp: stableTimestamp,
            status: 'filled' as TradeActivity['status'],
            order_type: 'market',
            execution_type: 'market' as 'market',
            order_source: 'vm_position',
            order_id: position.asset_id,
            limit_price: 0,
            stop_price: 0,
            trading_mode: 'PAPER',
            // Additional position data
            unrealized_pl: safeParseFloat(position.unrealized_pl, 0),
            unrealized_plpc: safeParseFloat(position.unrealized_plpc, 0),
            current_price: safeParseFloat(position.current_price, 0),
            market_value: safeParseFloat(position.market_value, 0),
            cost_basis: safeParseFloat(position.cost_basis, 0),
          });
        });
        
        // Process LIVE positions
        livePositions.forEach((position, index) => {
          const stableId = `position_${position.asset_id || position.symbol}_LIVE`;
          const stableTimestamp = position.created_at || '2025-06-17T13:24:00.000Z';
          
          positionTrades.push({
            id: stableId,
            strategy_id: position.strategy_id || 'vm_strategy',
            symbol: position.symbol || 'UNKNOWN',
            action: (parseFloat(position.qty || '0') > 0 ? 'buy' : 'sell') as 'buy' | 'sell',
            quantity: Math.abs(Math.round(safeParseFloat(position.qty || position.quantity, 1))),
            price: safeParseFloat(position.avg_entry_price || position.current_price, 0),
            timestamp: stableTimestamp,
            status: 'filled' as TradeActivity['status'],
            order_type: 'market',
            execution_type: 'market' as 'market',
            order_source: 'vm_position',
            order_id: position.asset_id,
            limit_price: 0,
            stop_price: 0,
            trading_mode: 'LIVE',
            // Additional position data
            unrealized_pl: safeParseFloat(position.unrealized_pl, 0),
            unrealized_plpc: safeParseFloat(position.unrealized_plpc, 0),
            current_price: safeParseFloat(position.current_price, 0),
            market_value: safeParseFloat(position.market_value, 0),
            cost_basis: safeParseFloat(position.cost_basis, 0),
          });
        });
        
        fetchedTrades.push(...positionTrades);
        console.log(`[TradingActivity] Created ${positionTrades.length} trades from positions`);
        console.log(`[TradingActivity] Position trade IDs:`, positionTrades.map(t => `${t.id} (${t.symbol} ${t.trading_mode})`));
      }
        
      // Use the handleLiveOrderUpdate function to merge polled data
      if (fetchedTrades.length > 0) {
        handleLiveOrderUpdate(fetchedTrades);
      }
    } catch (error) {
      console.warn('[TradingActivity] Polling update failed:', error);
    }
  }, [strategyId, handleLiveOrderUpdate]);

  // Setup polling as fallback for live updates
  const setupPolling = useCallback(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Make polling interval configurable via environment variable or props, default to 10 seconds
    const pollingInterval = parseInt(process.env.REACT_APP_POLLING_INTERVAL || '10000', 10);

    // Poll for live updates
    pollingIntervalRef.current = setInterval(pollForUpdates, pollingInterval);

    setLiveState(prev => ({
      ...prev,
      pollingActive: true,
      isLive: true
    }));

    console.log(`[TradingActivity] Polling enabled for live updates (${pollingInterval / 1000}s interval)`);
  }, [pollForUpdates]);

  // Stop live updates
  const stopLiveUpdates = useCallback(() => {
    // Clear polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Remove WebSocket handlers
    if (wsHandlersRegisteredRef.current) {
      vmTradingService.offWebSocketMessage('order_update');
      vmTradingService.offWebSocketMessage('portfolio_update');
      wsHandlersRegisteredRef.current = false;
    }

    setLiveState(prev => ({
      ...prev,
      isLive: false,
      pollingActive: false,
      wsConnected: false
    }));

    console.log('[TradingActivity] Live updates stopped');
  }, []);

  // Toggle live updates
  const toggleLiveUpdates = useCallback(() => {
    if (liveState.isLive) {
      stopLiveUpdates();
    } else {
      setupLiveUpdates();
    }
  }, [liveState.isLive, setupLiveUpdates, stopLiveUpdates]);

  // Use useCallback to memoize the fetchTradingActivity function
  const fetchTradingActivity = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log(`[TradingActivity] Fetching trades for mode: ${mode}, strategyId: ${strategyId}`);

      // Add timeout for localhost development to prevent hanging
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      const timeoutMs = isLocalhost ? 3000 : 10000; // 3 seconds for localhost, 10 seconds for production
      
      const withTimeout = (promise: Promise<any>) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
          )
        ]);
      };

      // Fetch from both PAPER and LIVE modes, including both all orders and filled orders specifically
      const [paperOrdersRaw, liveOrdersRaw, paperFilledOrders, liveFilledOrders, paperPositions, livePositions] = await Promise.all([
        withTimeout(vmTradingService.getOrders('PAPER')).catch(err => { console.warn('Failed to fetch PAPER orders', err); return []; }),
        withTimeout(vmTradingService.getOrders('LIVE')).catch(err => { console.warn('Failed to fetch LIVE orders', err); return []; }),
        withTimeout(vmTradingService.getOrders('PAPER', 'filled')).catch(err => { console.warn('Failed to fetch PAPER filled orders', err); return []; }),
        withTimeout(vmTradingService.getOrders('LIVE', 'filled')).catch(err => { console.warn('Failed to fetch LIVE filled orders', err); return []; }),
        withTimeout(vmTradingService.getPositions('PAPER')).catch(err => { console.warn('Failed to fetch PAPER positions', err); return []; }),
        withTimeout(vmTradingService.getPositions('LIVE')).catch(err => { console.warn('Failed to fetch LIVE positions', err); return []; })
      ]);

      // Merge all orders and filled orders (remove duplicates by ID)
      const allPaperOrdersRaw = [...paperOrdersRaw, ...paperFilledOrders].filter((order, index, self) => 
        index === self.findIndex(o => o.id === order.id)
      );
      const allLiveOrdersRaw = [...liveOrdersRaw, ...liveFilledOrders].filter((order, index, self) => 
        index === self.findIndex(o => o.id === order.id)
      );

      const paperOrders = allPaperOrdersRaw.map(o => ({ ...o, _tradingMode: 'PAPER' }));
      const liveOrders = allLiveOrdersRaw.map(o => ({ ...o, _tradingMode: 'LIVE' }));

      const allOrders = [...paperOrders, ...liveOrders];
      const allPositions = [...paperPositions, ...livePositions];
      
      console.log(`[TradingActivity] Fetched ${allOrders.length} total orders and ${allPositions.length} total positions.`);
      console.log(`[TradingActivity] Orders breakdown: PAPER=${paperOrders.length}, LIVE=${liveOrders.length}`);
      console.log(`[TradingActivity] Positions breakdown: PAPER=${paperPositions.length}, LIVE=${livePositions.length}`);

      // Create TradeActivity objects from orders
      const fetchedTrades: TradeActivity[] = allOrders.map((order, index) => {
        const associatedPosition = allPositions.find(p => p.symbol === order.symbol);
        const tradingMode = order._tradingMode;
        
        let orderPrice = 0;
        if (order.filled_avg_price) {
          orderPrice = safeParseFloat(order.filled_avg_price);
        } else if (associatedPosition?.avg_entry_price) {
          orderPrice = safeParseFloat(associatedPosition.avg_entry_price);
        } else if (order.price) {
          orderPrice = safeParseFloat(order.price);
        }

        return {
          id: order.id || `vm_order_${index}`,
          strategy_id: order.strategy_id || 'vm_strategy',
          symbol: order.symbol || 'UNKNOWN',
          action: (order.side === 'buy' || order.side === 'sell') ? order.side : 'buy',
          quantity: Math.round(safeParseFloat(order.qty || order.quantity, 1)),
          price: orderPrice,
          timestamp: order.created_at || order.timestamp || order.entry_timestamp || new Date().toISOString(),
          status: order.status as TradeActivity['status'] || 'pending',
          order_type: order.type || order.order_type || 'market',
          execution_type: order.type || order.execution_type || 'market',
          order_source: order.source || 'vm_trading',
          order_id: order.id,
          limit_price: safeParseFloat(order.limit_price),
          stop_price: safeParseFloat(order.stop_price),
          trading_mode: tradingMode || 'UNKNOWN',
        };
      });

      // If no orders but we have positions, create TradeActivity objects from positions
      if (allOrders.length === 0 && allPositions.length > 0) {
        console.log(`[TradingActivity] No orders found, creating trades from ${allPositions.length} positions`);
        
        const positionTrades: TradeActivity[] = [];
        
        // Process PAPER positions
        paperPositions.forEach((position, index) => {
          const stableId = `position_${position.asset_id || position.symbol}_PAPER`;
          const stableTimestamp = position.created_at || '2025-06-17T13:23:45.000Z';
          
          positionTrades.push({
            id: stableId,
            strategy_id: position.strategy_id || 'vm_strategy',
            symbol: position.symbol || 'UNKNOWN',
            action: (parseFloat(position.qty || '0') > 0 ? 'buy' : 'sell') as 'buy' | 'sell',
            quantity: Math.abs(Math.round(safeParseFloat(position.qty || position.quantity, 1))),
            price: safeParseFloat(position.avg_entry_price || position.current_price, 0),
            timestamp: stableTimestamp,
            status: 'filled' as TradeActivity['status'],
            order_type: 'market',
            execution_type: 'market' as 'market',
            order_source: 'vm_position',
            order_id: position.asset_id,
            limit_price: 0,
            stop_price: 0,
            trading_mode: 'PAPER',
            // Additional position data
            unrealized_pl: safeParseFloat(position.unrealized_pl, 0),
            unrealized_plpc: safeParseFloat(position.unrealized_plpc, 0),
            current_price: safeParseFloat(position.current_price, 0),
            market_value: safeParseFloat(position.market_value, 0),
            cost_basis: safeParseFloat(position.cost_basis, 0),
          });
        });
        
        // Process LIVE positions
        livePositions.forEach((position, index) => {
          const stableId = `position_${position.asset_id || position.symbol}_LIVE`;
          const stableTimestamp = position.created_at || '2025-06-17T13:24:00.000Z';
          
          positionTrades.push({
            id: stableId,
            strategy_id: position.strategy_id || 'vm_strategy',
            symbol: position.symbol || 'UNKNOWN',
            action: (parseFloat(position.qty || '0') > 0 ? 'buy' : 'sell') as 'buy' | 'sell',
            quantity: Math.abs(Math.round(safeParseFloat(position.qty || position.quantity, 1))),
            price: safeParseFloat(position.avg_entry_price || position.current_price, 0),
            timestamp: stableTimestamp,
            status: 'filled' as TradeActivity['status'],
            order_type: 'market',
            execution_type: 'market' as 'market',
            order_source: 'vm_position',
            order_id: position.asset_id,
            limit_price: 0,
            stop_price: 0,
            trading_mode: 'LIVE',
            // Additional position data
            unrealized_pl: safeParseFloat(position.unrealized_pl, 0),
            unrealized_plpc: safeParseFloat(position.unrealized_plpc, 0),
            current_price: safeParseFloat(position.current_price, 0),
            market_value: safeParseFloat(position.market_value, 0),
            cost_basis: safeParseFloat(position.cost_basis, 0),
          });
        });
        
        fetchedTrades.push(...positionTrades);
        console.log(`[TradingActivity] Created ${positionTrades.length} trades from positions`);
        console.log(`[TradingActivity] Position trade IDs:`, positionTrades.map(t => `${t.id} (${t.symbol} ${t.trading_mode})`));
      }

      // Filter based on mode
      let filteredTrades = fetchedTrades;
      if (mode === 'strategy' && strategyId) {
        filteredTrades = fetchedTrades.filter(trade => trade.strategy_id === strategyId);
      } else if (mode === 'manual') {
        filteredTrades = fetchedTrades.filter(trade => 
          trade.strategy_id === 'manual' || trade.order_source === 'manual' || trade.order_source === 'stewietrader_ui'
        );
      }
      // mode === 'all' gets all trades

      console.log(`[TradingActivity] Final filtered trades (${filteredTrades.length}):`, filteredTrades);
      setTrades(filteredTrades);
      console.log(`[TradingActivity] ✅ Loaded ${filteredTrades.length} trades from VM (filtered from ${fetchedTrades.length} total)`);
    } catch (err: any) {
      console.error('[TradingActivity] Error fetching trading activity from VM:', err);
      setError(err.message || 'Failed to fetch trading activity from VM. Please check VM connection.');
      setTrades([]); // Ensure trades is empty on error
    } finally {
      setLoading(false);
    }
  }, [strategyId, mode]);

  // Call fetchTradingActivity when the component mounts or dependencies change
  useEffect(() => {
    fetchTradingActivity();
  }, [fetchTradingActivity]);

  // Setup live updates on mount and cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;
    setupLiveUpdates();

    return () => {
      componentMountedRef.current = false;
      stopLiveUpdates();
    };
  }, [setupLiveUpdates, stopLiveUpdates]);

  const filteredTrades = trades.filter(trade => {
    if (filters.status === 'all') return true;
    return trade.status === filters.status;
  });

  const sortedTrades = [...filteredTrades].sort((a, b) => {
    const order = filters.sortOrder === 'asc' ? 1 : -1;
    switch (filters.sortBy) {
      case 'symbol':
        return a.symbol.localeCompare(b.symbol) * order;
      case 'price':
        return (a.price - b.price) * order;
      case 'quantity':
        return (a.quantity - b.quantity) * order;
      case 'timestamp':
      default:
        return (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) * order;
    }
  });

  const totalPages = Math.ceil(sortedTrades.length / tradesPerPage);
  const startIndex = (currentPage - 1) * tradesPerPage;
  const endIndex = startIndex + tradesPerPage;
  const currentPageTrades = sortedTrades.slice(startIndex, endIndex);

  const handleStatusChange = (status: TradeActivity['status'] | 'all') => {
    setFilters(prev => ({ ...prev, status }));
    setCurrentPage(1);
  };

  const handleSortChange = (sortBy: TradeFilters['sortBy']) => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getBadgeVariant = (status: TradeActivity['status']) => {
    switch(status) {
      case 'pending':
      case 'open':
        return 'outline';
      case 'filled':
      case 'executed':
        return 'success';
      case 'canceled':
      case 'cancelled':
        return 'secondary';
      case 'rejected':
      case 'failed':
      case 'closed':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const getDirectionIcon = (action: string) => {
    return action === 'buy' ? <ChevronUp className="h-4 w-4 text-green-500" /> : <ChevronDown className="h-4 w-4 text-red-500" />;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Determine title based on mode
  const getTitle = () => {
    if (title) return title;
    switch (mode) {
      case 'manual':
        return 'Direct Order Activity';
      case 'all':
        return 'All Trading Activity';
      case 'strategy':
      default:
        return 'Trading Activity';
    }
  };

  // Get live status indicator
  const getLiveStatusIndicator = () => {
    if (!liveState.isLive) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-200">
          <WifiOff className="h-3 w-3 mr-1" />
          Offline
        </div>
      );
    }

    if (liveState.wsConnected) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 border border-green-200">
          <Wifi className="h-3 w-3 mr-1" />
          Live (WebSocket)
        </div>
      );
    }

    if (liveState.pollingActive) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 border border-blue-200">
          <Radio className="h-3 w-3 mr-1 animate-pulse" />
          Live (Polling)
        </div>
      );
    }

    return (
      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700 border border-yellow-200">
        <Clock className="h-3 w-3 mr-1" />
        Connecting...
      </div>
    );
  };

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-3">
          {getTitle()}
          {getLiveStatusIndicator()}
        </CardTitle>
        <div className="flex flex-col items-end space-y-1">
                    {mode !== 'strategy' && (
            <div className="text-xs text-muted-foreground">
              {mode === 'manual' ? 'Manual orders from OrderDialog' : 'All trades including strategies and manual orders'}
            </div>
          )}
          {liveState.lastUpdate && (
            <div className="text-xs text-muted-foreground">
              Last update: {format(liveState.lastUpdate, 'HH:mm:ss')} ({liveState.updateCount} updates)
            </div>
          )}
          {vmSimulationMode && (
            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 border border-yellow-200">
              ⚠️ VM Simulation Mode - Orders endpoint unavailable
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Error State */}
        {error && (
          <div className="p-8 text-center text-red-600 space-y-3">
            <div className="flex justify-center">
              <AlertCircle className="h-10 w-10" />
            </div>
            <p className="font-medium">{error}</p>
            <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={fetchTradingActivity}>
              Retry
            </Button>
              <Button 
                variant="outline" 
                onClick={toggleLiveUpdates}
                className="flex items-center gap-2"
              >
                {liveState.isLive ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
                {liveState.isLive ? 'Stop Live' : 'Start Live'}
              </Button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !error && (
          <div className="p-8 text-center space-y-3">
            <div className="flex justify-center">
              <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
            </div>
            <p className="text-gray-500">Loading trading activity...</p>
          </div>
        )}

        {/* Content when data is loaded */}
        {!loading && !error && (
          <>
            <div className="flex items-center justify-between p-4">
              <Select value={filters.status} onValueChange={(value) => handleStatusChange(value as TradeActivity['status'] | 'all')}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="filled">Filled</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="executed">Executed</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchTradingActivity}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                
                <Button 
                  variant={liveState.isLive ? "destructive" : "default"}
                  size="sm"
                  onClick={toggleLiveUpdates}
                  className="flex items-center gap-2"
                >
                  {liveState.isLive ? (
                    <>
                      <WifiOff className="h-4 w-4" />
                      Stop Live
                    </>
                  ) : (
                    <>
                      <Wifi className="h-4 w-4" />
                      Start Live
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Empty State - Only shown when data is loaded successfully but no trades exist */}
            {trades.length === 0 ? (
              <div className="p-12 text-center space-y-3">
                <div className="flex justify-center">
                  <Clock className="h-16 w-16 text-gray-300" />
                </div>
                <p className="text-xl font-medium text-gray-700">No Trading Activity</p>
                <p className="text-gray-500 max-w-md mx-auto">
                  {strategyId 
                    ? "There are no trades for this strategy yet. Trades will appear here after your first trade execution."
                    : "Please select a strategy to view its trading activity."}
                </p>
                {liveState.isLive && (
                  <p className="text-blue-600 text-sm">
                    🔴 Live updates enabled - New orders will appear automatically
                  </p>
                )}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">
                        <Button variant="ghost" onClick={() => handleSortChange('timestamp')} className="justify-start">
                          Timestamp
                          {filters.sortBy === 'timestamp' && (filters.sortOrder === 'asc' ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />)}
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button variant="ghost" onClick={() => handleSortChange('symbol')} className="justify-start">
                          Symbol
                          {filters.sortBy === 'symbol' && (filters.sortOrder === 'asc' ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />)}
                        </Button>
                      </TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>
                        <Button variant="ghost" onClick={() => handleSortChange('price')} className="justify-start">
                          Price
                          {filters.sortBy === 'price' && (filters.sortOrder === 'asc' ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />)}
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button variant="ghost" onClick={() => handleSortChange('quantity')} className="justify-start">
                          Quantity
                          {filters.sortBy === 'quantity' && (filters.sortOrder === 'asc' ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />)}
                        </Button>
                      </TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentPageTrades.length > 0 ? (
                      currentPageTrades.map((trade, index) => (
                        <TableRow 
                          key={trade.id || index}
                          className={trade.is_live_update ? 'bg-green-50 border-l-4 border-l-green-500' : ''}
                        >
                          <TableCell className="font-medium">{formatDate(trade.timestamp)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {trade.symbol}
                              {trade.is_live_update && (
                                <div className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-green-100 text-green-700">
                                  LIVE
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              {getDirectionIcon(trade.action)}
                              {trade.action.toUpperCase()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <DollarSign className="h-4 w-4 mr-1" />
                              {trade.price.toFixed(2)}
                            </div>
                          </TableCell>
                          <TableCell>{trade.quantity}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={trade.trading_mode === 'LIVE' ? 'destructive' : 'secondary'}
                              className="whitespace-nowrap px-1 py-0.5 rounded text-xs font-medium"
                            >
                              {trade.trading_mode || 'UNKNOWN'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={getBadgeVariant(trade.status) as any} 
                              className="whitespace-nowrap px-2 py-1 rounded-sm font-normal capitalize"
                            >
                              {/* Handle status compatibility: map 'executed' to 'filled' for display */}
                              {trade.status === 'executed' ? 'filled' : trade.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          <p className="text-gray-500">No trades match the current filter.</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Only show pagination if we have trades */}
                {currentPageTrades.length > 0 && (
                  <div className="flex justify-center items-center p-4">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="icon"
                      className="mr-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span>{`Page ${currentPage} of ${totalPages}`}</span>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="icon"
                      className="ml-2"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TradingActivity;
